// Coach read-path profile projection (CO-8 / CO-11).
//
// The coach console needs a bit of an assigned athlete's profile to render the
// team dashboard: the athlete's display NAME, which program is pinned
// (currentProgramId → adherence meter), the week-start day (target math), and
// the unit preference (display). None of these are health special-category
// data, so the active coach edge is sufficient (contrast the health fields on
// Session, which gate on a separate per-metric consent — coachReadProjection).
//
// This is an ALLOWLIST, deliberately — the Profile row also carries sensitive
// fields (painMap, painHistory, bodyweightKg, sex, ageBucket, glucoseUnit…) and
// a denylist would leak any NEW sensitive field the moment it's added to the
// schema. With an allowlist, a future field is invisible to the coach until
// someone consciously adds it here. Pure, RN/web-portable, no DDB import.

import type { Profile } from "./schemas/profile.js";

// The only Profile fields a coach may read across the edge. Add here — and only
// here — when a new NON-sensitive field genuinely needs to reach the console.
export const COACH_VISIBLE_PROFILE_FIELDS = [
  "name",
  "currentProgramId",
  "weekStartDay",
  "unitsPreference",
] as const satisfies readonly (keyof Profile)[];

export type CoachVisibleProfile = { sub: string } & Pick<
  Profile,
  (typeof COACH_VISIBLE_PROFILE_FIELDS)[number]
>;

/** Project an athlete's Profile down to the coach-visible allowlist. */
export const pickCoachProfileFields = (
  sub: string,
  profile: Profile,
): CoachVisibleProfile => {
  const out = { sub } as CoachVisibleProfile;
  for (const f of COACH_VISIBLE_PROFILE_FIELDS) {
    const v = profile[f];
    if (v !== undefined) (out as Record<string, unknown>)[f] = v;
  }
  return out;
};

// The REVERSE direction (#331): an athlete reading their COACH's profile. The
// athlete side needs the coach's display name only (the coach-edges list, the
// "assigned by" badge), so it gets only that. The coach's pinned program, week
// start and units are the coach's own business, not their athlete's. Same
// allowlist rule as above: a field reaches the athlete only once listed here.
export const ATHLETE_VISIBLE_COACH_PROFILE_FIELDS = [
  "name",
] as const satisfies readonly (keyof Profile)[];

export type AthleteVisibleCoachProfile = { sub: string } & Pick<
  Profile,
  (typeof ATHLETE_VISIBLE_COACH_PROFILE_FIELDS)[number]
>;

/** Project a coach's Profile down to what their athlete may read. */
export const pickAthleteVisibleCoachProfile = (
  sub: string,
  profile: Profile,
): AthleteVisibleCoachProfile => {
  const out = { sub } as AthleteVisibleCoachProfile;
  for (const f of ATHLETE_VISIBLE_COACH_PROFILE_FIELDS) {
    const v = profile[f];
    if (v !== undefined) (out as Record<string, unknown>)[f] = v;
  }
  return out;
};
